/**
 * Reading the OpenStreetMap "Shortbread" vector tile schema.
 *
 * Only the layers this game renders are described here. The full schema has 26
 * layers; see the source's tilejson for the rest.
 */

/** What a landcover polygon becomes in the world. */
export type LandClass = 'forest' | 'grass' | 'farmland' | 'bare' | 'ignore';

export const SHORTBREAD_LAYERS = {
  land: 'land',
  water: 'water_polygons',
  ocean: 'ocean',
  waterLines: 'water_lines',
  streets: 'streets',
  buildings: 'buildings',
  /** Bridges mapped as areas (man_made=bridge): the deck's outline. */
  bridges: 'bridges',
} as const;

/**
 * `land.kind` values that carry trees.
 *
 * Shortbread collapses OSM's `natural=wood` and `landuse=forest` into `forest`.
 * `scrub` is included because in this landscape it is dense enough to read as
 * vegetation, though it is scattered at a lower density.
 */
const FOREST_KINDS = new Set(['forest', 'wood']);
const SCRUB_KINDS = new Set(['scrub', 'heath']);
const GRASS_KINDS = new Set([
  'grass',
  'meadow',
  'grassland',
  'park',
  'garden',
  'village_green',
  'allotments',
  'recreation_ground',
  'golf_course',
]);
const FARM_KINDS = new Set(['farmland', 'farmyard', 'orchard', 'vineyard']);
const BARE_KINDS = new Set(['bare_rock', 'scree', 'sand', 'beach', 'quarry', 'glacier']);

export function classifyLand(kind: unknown): LandClass {
  if (typeof kind !== 'string') return 'ignore';
  if (FOREST_KINDS.has(kind) || SCRUB_KINDS.has(kind)) return 'forest';
  if (GRASS_KINDS.has(kind)) return 'grass';
  if (FARM_KINDS.has(kind)) return 'farmland';
  if (BARE_KINDS.has(kind)) return 'bare';
  return 'ignore';
}

/** Trees per square metre, by what the polygon is. */
export function treeDensity(kind: unknown): number {
  if (typeof kind !== 'string') return 0;
  if (FOREST_KINDS.has(kind)) return 1 / 260;
  // Scrub is vegetation but not canopy: sparser and, in the renderer, shorter.
  if (SCRUB_KINDS.has(kind)) return 1 / 900;
  return 0;
}

/** What a street becomes on the ground. */
export type RoadClass = 'major' | 'road' | 'minor' | 'track' | 'path' | 'rail' | 'ignore';

/**
 * Half-width in metres, by road class. These are carriageway widths, not
 * right-of-way: a Swedish `residential` street is about 5.5 m kerb to kerb.
 */
const ROAD_HALF_WIDTH: Record<RoadClass, number> = {
  major: 7,
  road: 4.25,
  minor: 2.75,
  track: 1.6,
  path: 0.75,
  rail: 1.4,
  ignore: 0,
};

const MAJOR_KINDS = new Set(['motorway', 'trunk', 'primary']);
const ROAD_KINDS = new Set(['secondary', 'tertiary']);
const MINOR_KINDS = new Set([
  'residential',
  'unclassified',
  'living_street',
  'service',
  'pedestrian',
]);
const TRACK_KINDS = new Set(['track']);
const PATH_KINDS = new Set(['path', 'footway', 'cycleway', 'bridleway', 'steps']);

/**
 * Classify a street feature.
 *
 * `rail` is a separate boolean in the schema rather than a `kind` value, so it
 * has to be checked before the kind lookup — railway features carry kinds like
 * `rail` and `tram` that would otherwise fall through to `ignore`.
 */
export function classifyRoad(properties: Record<string, unknown>): RoadClass {
  if (properties.rail === true) return 'rail';
  const kind = properties.kind;
  if (typeof kind !== 'string') return 'ignore';
  if (MAJOR_KINDS.has(kind)) return 'major';
  if (ROAD_KINDS.has(kind)) return 'road';
  if (MINOR_KINDS.has(kind)) return 'minor';
  if (TRACK_KINDS.has(kind)) return 'track';
  if (PATH_KINDS.has(kind)) return 'path';
  return 'ignore';
}

export function roadHalfWidth(roadClass: RoadClass): number {
  return ROAD_HALF_WIDTH[roadClass];
}

/**
 * How tall to make a building.
 *
 * Shortbread's `buildings` layer carries **no attributes at all** — no height,
 * no `building:levels`, not even the building type. So height cannot be read;
 * it has to be invented. This maps footprint area onto a plausible height for a
 * rural/small-town Swedish landscape, with the caller adding jitter from the
 * tile-seeded PRNG so a street of similar houses is not perfectly uniform.
 *
 * The fallback for a building with no OSM tags: tagged buildings are profiled
 * from their type, storeys and height instead. See `build/buildingprofile.ts`.
 */
export function buildingHeightForArea(area: number): { base: number; jitter: number } {
  if (area < 45) return { base: 2.8, jitter: 0.8 };   // sheds, garages
  if (area < 160) return { base: 5.2, jitter: 1.8 };  // houses
  if (area < 600) return { base: 6.4, jitter: 2.2 };  // large houses, small blocks
  // Deliberately *not* taller than the band above. Height is not monotonic in
  // footprint outside a city: the biggest rural footprints are agricultural and
  // industrial halls — the spawn tile has several over 3000 m^2 — which are
  // wide and low. Extrapolating "bigger means taller" puts 15 m towers across
  // farmland, which is the single most obviously wrong thing in the scene.
  return { base: 7.0, jitter: 2.4 };
}

// --- road styles -------------------------------------------------------------

/**
 * How a street is drawn, finer than `RoadClass`: which surface, which
 * markings, how wide and how high above its neighbours. Ids are small integers
 * because they ride on every ribbon vertex into the road shader, which keeps
 * the matching table in `three/roadMaterial.ts`.
 */
export const ROAD_STYLE = {
  motorway: 0,
  arterial: 1,
  collector: 2,
  street: 3,
  service: 4,
  pedestrian: 5,
  footway: 6,
  cycleway: 7,
  path: 8,
  track: 9,
  steps: 10,
  rail: 11,
  /** Street tramway: only its two rails are drawn, on whatever it runs over. */
  tram: 12,
} as const;

export type RoadStyle = (typeof ROAD_STYLE)[keyof typeof ROAD_STYLE];

/** Half-width per style, metres, kerb to kerb. */
export const ROAD_STYLE_HALF_WIDTH: Record<RoadStyle, number> = {
  0: 7.5, 1: 5.5, 2: 4.25, 3: 2.9, 4: 2.1, 5: 3, 6: 1.1, 7: 1.2, 8: 0.7, 9: 1.7, 10: 1.1, 11: 1.6, 12: 0.85,
};

/**
 * Lift above the terrain per style, metres. Different per class so that where
 * a footway meets a street the two surfaces are never coplanar — the bigger
 * road always wins the junction, which is also how a kerb reads.
 *
 * Engineered roads (0–4, 11) carry the ground up to just under this surface
 * (`UNDER_ROAD` in `roadprofile.ts`), so for them it only orders junctions.
 * The rest lie on the ground at this height: all of them under `UNDER_ROAD`,
 * so a footway crossing a street runs under it, and a sidewalk beside a
 * street sits at kerb level instead of on a strip above the grass. A tram's
 * rails alone are just over `UNDER_ROAD`, so they lie on top of the street
 * they run along.
 */
export const ROAD_STYLE_LIFT: Record<RoadStyle, number> = {
  0: 0.3, 1: 0.28, 2: 0.26, 3: 0.24, 4: 0.23, 5: 0.08, 6: 0.05, 7: 0.06, 8: 0.03, 9: 0.04, 10: 0.07, 11: 0.32, 12: 0.12,
};

const STYLE_BY_KIND: Record<string, RoadStyle> = {
  motorway: 0, trunk: 0,
  primary: 1,
  secondary: 2, tertiary: 2,
  residential: 3, unclassified: 3, living_street: 3, road: 3,
  service: 4,
  pedestrian: 5,
  footway: 6, bridleway: 6, platform: 6,
  cycleway: 7,
  path: 8,
  track: 9,
  steps: 10,
};

/** The draw style for a street feature, or undefined to skip it. */
export function roadStyle(properties: Record<string, unknown>): RoadStyle | undefined {
  // A tramway is two rails set into the street — or into a bridge's deck,
  // which `bridges.ts` lays under a tram bridge of its own.
  if (properties.rail === true) return properties.kind === 'tram' ? ROAD_STYLE.tram : ROAD_STYLE.rail;
  const kind = properties.kind;
  if (typeof kind !== 'string') return undefined;
  return STYLE_BY_KIND[kind];
}

/**
 * Half-width of one street feature, metres.
 *
 * `ROAD_STYLE_HALF_WIDTH` is a two-way road of the class. Most of a motorway
 * is not that: OSM maps each carriageway as its own one-way line, and every
 * slip road as a `link` — a single lane. Drawn at two-way motorway width, an
 * interchange became a pile of fifteen-metre slabs, each ramp as wide as the
 * motorway it leaves. So one-way carriageways and links are narrowed to what
 * they carry: two lanes and a hard shoulder, or one lane and its verges.
 */
export function streetHalfWidth(style: RoadStyle, properties: Record<string, unknown>): number {
  // A mapped physical width wins over class estimates, especially in narrow
  // passages. `maxwidth` is a vehicle restriction, not the corridor's width.
  const raw = properties.width;
  const metres = typeof raw === 'number' ? raw
    : typeof raw === 'string' && /^\s*\d+(?:\.\d+)?\s*(?:m)?\s*$/.test(raw) ? parseFloat(raw) : NaN;
  if (Number.isFinite(metres) && metres >= 0.4 && metres <= 60) return metres / 2;
  const full = ROAD_STYLE_HALF_WIDTH[style];
  if (style > ROAD_STYLE.service) return full;
  if (properties.link === true) {
    return style === ROAD_STYLE.motorway ? 3.3 : Math.max(2.4, full * 0.6);
  }
  if (properties.oneway === true) {
    return style === ROAD_STYLE.motorway ? 5.6 : Math.max(2.2, full * 0.75);
  }
  return full;
}

/** What a water body is, for how far under its banks it lies. See `placeVectorChunk`. */
export const WATER_KIND = { still: 0, flowing: 1, sea: 2 } as const;

/** Half-width of a mapped waterway line, metres, or 0 to skip it. */
export function waterLineHalfWidth(kind: unknown): number {
  switch (kind) {
    case 'river': return 4.5;
    case 'canal': return 4;
    case 'stream': return 1.1;
    case 'ditch':
    case 'drain': return 0.6;
    default: return 0;
  }
}

// --- landcover ---------------------------------------------------------------

/**
 * Landcover classes painted onto the terrain. Ids ride in a per-tile raster to
 * the terrain shader, which holds the matching look-up — keep the two in step.
 * Higher `LANDCOVER_PRIORITY` draws over lower where polygons overlap: a
 * playground inside a park is a playground.
 */
export const LANDCOVER = {
  none: 0,
  lawn: 1,
  meadow: 2,
  wheat: 3,
  crop: 4,
  ploughed: 5,
  rapeseed: 6,
  orchard: 7,
  allotments: 8,
  sand: 9,
  rock: 10,
  wetland: 11,
  heath: 12,
  cemetery: 13,
  playground: 14,
  turf: 15,
  parking: 16,
  paving: 17,
  dirt: 18,
  gravel: 19,
  concrete: 20,
  golf: 21,
  /**
   * The unmapped ground of a dense town: pavement, verges, courtyards.
   * Inferred from building density, not mapped. See `build/urbanground.ts`.
   */
  urban: 22,
  /** A house's garden: mown lawn, inferred from the plots. See `build/plots.ts`. */
  garden: 23,
  /** A driveway's asphalt. */
  drive: 24,
  /**
   * A city canal's or harbour's water between quay walls: the terrain is not
   * drawn there, as the ground is kept at the street's level right up to the
   * wall (see `build/quays.ts`). Painted by placement, never from polygons.
   */
  quayWater: 25,
} as const;

export type LandcoverClass = (typeof LANDCOVER)[keyof typeof LANDCOVER];

export const LANDCOVER_PRIORITY: Record<LandcoverClass, number> = {
  // Town ground lies under everything mapped: a park in town is a park.
  0: 0, 22: 0.5, 20: 1, 3: 2, 4: 2, 5: 2, 6: 2, 2: 3, 12: 4, 11: 5, 7: 6, 8: 7, 1: 8, 21: 9, 13: 10,
  9: 11, 10: 12, 18: 13, 19: 14, 14: 15, 15: 16, 17: 17, 16: 18,
  // Painted after the rest, never from polygons: listed for completeness.
  23: 0.6, 24: 0.7, 25: 99,
};

const LAND_LANDCOVER: Record<string, LandcoverClass> = {
  grass: 1, park: 1, garden: 1, village_green: 1, recreation_ground: 1, dog_park: 1,
  meadow: 2, grassland: 2, greenfield: 2,
  farmland: 3,
  orchard: 7, vineyard: 7, plant_nursery: 7, greenhouse_horticulture: 7,
  allotments: 8,
  sand: 9, beach: 9, dune: 9,
  bare_rock: 10, scree: 10, shingle: 10, quarry: 10,
  bog: 11, marsh: 11, swamp: 11, wet_meadow: 11, string_bog: 11, wetland: 11,
  heath: 12, scrub: 12,
  cemetery: 13, grave_yard: 13,
  playground: 14,
  pitch: 15,
  brownfield: 18, landfill: 18, construction: 18, farmyard: 18,
  railway: 19,
  industrial: 20, garages: 20,
  commercial: 22, retail: 22,
  golf_course: 21, miniature_golf: 21,
};

const SITE_LANDCOVER: Record<string, LandcoverClass> = {
  parking: 16,
  bicycle_parking: 17,
  construction: 18,
  sports_centre: 15,
  school: 1, university: 1, college: 1, hospital: 1, kindergarten: 1,
};

/**
 * The landcover class for a polygon from the `land`, `sites` or
 * `street_polygons` layer. Farmland is split across four crops by `variant`,
 * so a patchwork of fields reads as a patchwork.
 */
export function landcoverClass(layer: 'land' | 'sites' | 'street_polygons', kind: unknown, variant = 0): LandcoverClass {
  if (typeof kind !== 'string') return LANDCOVER.none;
  if (layer === 'street_polygons') return kind === 'pedestrian' ? LANDCOVER.paving : LANDCOVER.concrete;
  const table = layer === 'land' ? LAND_LANDCOVER : SITE_LANDCOVER;
  const cls = table[kind] ?? LANDCOVER.none;
  if (cls === LANDCOVER.wheat) {
    return ([LANDCOVER.wheat, LANDCOVER.crop, LANDCOVER.ploughed, LANDCOVER.rapeseed] as const)[variant % 4];
  }
  return cls;
}
